import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  purchaseOrder: string;
  billedFrom: {
    name: string;
    logo: string;
    addressLines: string[];
    email: string;
    phone: string;
    gstin: string;
    cin: string;
  };
  billedTo: {
    companyName: string;
    ownerName?: string;
    address?: string;
    city?: string;
    pinCode?: string | number;
    state?: string;
    country?: string;
    email: string;
    phone?: string;
    mobile?: string;
    gstNumber?: string;
  };
  items: Array<{
    description: string;
    hsnSac: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
  subtotal: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  grandTotal: number;
  balanceDue: number;
  notes?: string;
  termsConditions?: string;
}

// Function to convert number to words for Indian currency
const numberToWords = (num: number): string => {
  if (num === 0) return 'Zero Rupees Only';

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return '';
    
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const one = n % 10;
      return tens[ten] + (one > 0 ? ' ' + ones[one] : '');
    }
    
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    return ones[hundred] + ' Hundred' + (rest > 0 ? ' ' + convertLessThanThousand(rest) : '');
  };

  // Split into rupees and paise
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);

  let result = '';

  if (rupees > 0) {
    // Handle crores
    const crore = Math.floor(rupees / 10000000);
    let remainder = rupees % 10000000;

    if (crore > 0) {
      result += convertLessThanThousand(crore) + ' Crore ';
    }

    // Handle lakhs
    const lakh = Math.floor(remainder / 100000);
    remainder = remainder % 100000;

    if (lakh > 0) {
      result += convertLessThanThousand(lakh) + ' Lakh ';
    }

    // Handle thousands
    const thousand = Math.floor(remainder / 1000);
    remainder = remainder % 1000;

    if (thousand > 0) {
      result += convertLessThanThousand(thousand) + ' Thousand ';
    }

    // Handle remaining
    if (remainder > 0) {
      result += convertLessThanThousand(remainder) + ' ';
    }

    result += 'Rupees';
  }

  if (paise > 0) {
    if (result) result += ' and ';
    result += convertLessThanThousand(paise) + ' Paise';
  }

  return result.trim() + ' Only';
};

export const generateInvoicePDF = async (invoiceData: InvoiceData, action: 'download' | 'preview' = 'download') => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Helper function to add text with auto-wrapping
  const addWrappedText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10, color: [number, number, number] = [0, 0, 0]) => {
    doc.setFontSize(fontSize);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * fontSize * 0.35); // Return new Y position
  };

  // TAX INVOICE title (right side at top)
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('TAX INVOICE', pageWidth - margin, yPos + 5, { align: 'right' });

  // Invoice number
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice No: ${invoiceData.invoiceNumber}`, pageWidth - margin, yPos + 12, { align: 'right' });

  // Balance Due (right side)
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Balance Due', pageWidth - margin, yPos + 18, { align: 'right' });
  doc.setFontSize(14);
  doc.setTextColor(225, 71, 50);
  doc.text(`${invoiceData.balanceDue.toFixed(2)} INR`, pageWidth - margin, yPos + 24, { align: 'right' });

  // Add company logo on the left (above company name)
  try {
    // Load logo image
    const logo = new Image();
    logo.src = invoiceData.billedFrom.logo;
    await new Promise((resolve, reject) => {
      logo.onload = resolve;
      logo.onerror = reject;
    });
    doc.addImage(logo, 'PNG', margin, yPos, 15, 15);
    yPos += 18; // Add spacing after logo
  } catch (error) {
    console.error('Error loading logo:', error);
    yPos += 5; // Add some spacing even if logo fails
  }

  // // BILLED FROM section label
  // doc.setFontSize(8);
  // doc.setFont('helvetica', 'bold');
  // doc.setTextColor(100, 100, 100);
  // doc.text('BILLED FROM', margin, yPos);
  // yPos += 5;

  // Company name
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(225, 71, 50); // Red color
  doc.text(invoiceData.billedFrom.name.toUpperCase(), margin, yPos);
  yPos += 6;

  // Company address
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  invoiceData.billedFrom.addressLines.forEach((line) => {
    doc.text(line, margin, yPos);
    yPos += 3.5;
  });

  // Company contact details
  doc.setFontSize(8);
  doc.text(`CIN No ${invoiceData.billedFrom.cin}`, margin, yPos);
  yPos += 3.5;
  doc.text(`GSTIN ${invoiceData.billedFrom.gstin}`, margin, yPos);
  yPos += 3.5;
  doc.text(`Phone ${invoiceData.billedFrom.phone}`, margin, yPos);
  yPos += 3.5;
  doc.text(invoiceData.billedFrom.email, margin, yPos);
  yPos += 6;

  // Bill To section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 100, 100);
  doc.text('Bill To', margin, yPos);
  yPos += 5;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(invoiceData.billedTo.companyName, margin, yPos);
  yPos += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  if (invoiceData.billedTo.ownerName) {
    doc.text(invoiceData.billedTo.ownerName, margin, yPos);
    yPos += 4;
  }

  if (invoiceData.billedTo.address) {
    const addressText = `${invoiceData.billedTo.address}`;
    const addressLines = doc.splitTextToSize(addressText, 80);
    doc.text(addressLines, margin, yPos);
    yPos += addressLines.length * 4;
  }

  if (invoiceData.billedTo.city) {
    const cityLine = `${invoiceData.billedTo.city}${invoiceData.billedTo.pinCode ? ' - ' + invoiceData.billedTo.pinCode : ''}, ${invoiceData.billedTo.state || ''}, ${invoiceData.billedTo.country || ''}`;
    doc.text(cityLine, margin, yPos);
    yPos += 4;
  }

  if (invoiceData.billedTo.email) {
    doc.text(invoiceData.billedTo.email, margin, yPos);
    yPos += 4;
  }

  if (invoiceData.billedTo.gstNumber) {
    doc.text(`GSTIN ${invoiceData.billedTo.gstNumber}`, margin, yPos);
    yPos += 4;
  }

  // Invoice details (right side)
  const detailsX = pageWidth / 2 + 10;
  let detailsY = yPos - (invoiceData.billedTo.ownerName ? 25 : 21);

  // Add bordered boxes for invoice details
  const addDetailRow = (label: string, value: string) => {
    doc.setFillColor(245, 245, 245);
    doc.rect(detailsX, detailsY - 3.5, 35, 6, 'F');
    doc.rect(detailsX + 35, detailsY - 3.5, 45, 6, 'S');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text(label, detailsX + 2, detailsY);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(value, detailsX + 37, detailsY);
    
    detailsY += 6;
  };

  addDetailRow('Invoice Date', new Date(invoiceData.invoiceDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).replace(/\//g, '/'));

  addDetailRow('Terms', invoiceData.dueDate ? 'Due on Receipt' : 'Due on Receipt');

  addDetailRow('Due Date', new Date(invoiceData.dueDate).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).replace(/\//g, '/'));

  if (invoiceData.purchaseOrder) {
    addDetailRow('P.O.#', invoiceData.purchaseOrder);
  }

  yPos = Math.max(yPos, detailsY) + 5;

  // Place of Supply
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Place Of Supply: ${invoiceData.billedTo.state || 'Haryana'} (06)`, margin, yPos);
  yPos += 8;

  // Items table
  const tableData = invoiceData.items.map((item, index) => [
    (index + 1).toString(),
    item.description,
    item.hsnSac || '',
    item.quantity.toString(),
    item.rate.toFixed(2),
    item.amount.toFixed(2)
  ]);

  // Calculate available width: pageWidth - left margin - right margin
  const availableWidth = pageWidth - (2 * margin); // 180mm total

  autoTable(doc, {
    startY: yPos,
    head: [['#', 'Item & Description', 'HSN/SAC', 'Qty', 'Rate', 'Amount (INR)']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [60, 60, 60],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [0, 0, 0],
      cellPadding: 3,
      lineColor: [200, 200, 200],
      lineWidth: 0.1
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },       // # - 10mm
      1: { halign: 'left', cellWidth: 70 },         // Item & Description - 70mm
      2: { halign: 'center', cellWidth: 25 },       // HSN/SAC - 25mm
      3: { halign: 'center', cellWidth: 15 },       // Qty - 15mm
      4: { halign: 'right', cellWidth: 30 },        // Rate - 30mm
      5: { halign: 'right', cellWidth: 30 }         // Amount (INR) - 30mm - Total: 180mm
    },
    margin: { left: margin, right: margin },
    tableWidth: availableWidth,
    styles: {
      overflow: 'linebreak',
      cellWidth: 'wrap'
    }
  });

  // Get Y position after table
  yPos = (doc as any).lastAutoTable.finalY + 5;

  // Totals section
  const totalsX = pageWidth - margin - 60;
  const totalsWidth = 60;

  const addTotalRow = (label: string, value: string, isBold: boolean = false, bgColor: [number, number, number] | null = null) => {
    if (bgColor) {
      doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      doc.rect(totalsX, yPos - 3.5, totalsWidth, 6, 'F');
    }
    
    doc.setFontSize(9);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(label, totalsX + 2, yPos);
    doc.text(value, totalsX + totalsWidth - 2, yPos, { align: 'right' });
    yPos += 6;
  };

  addTotalRow('Sub Total', `${invoiceData.subtotal.toFixed(2)}`);
  
  if (invoiceData.discountAmount > 0) {
    addTotalRow('Discount', `-${invoiceData.discountAmount.toFixed(2)}`);
  }

  addTotalRow(`IGST ${invoiceData.taxPercentage}%`, `${invoiceData.taxAmount.toFixed(2)}`);
  
  // Total with background
  doc.setFillColor(60, 60, 60);
  doc.rect(totalsX, yPos - 3.5, totalsWidth, 7, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('Total', totalsX + 2, yPos);
  doc.text(`${invoiceData.grandTotal.toFixed(2)}`, totalsX + totalsWidth - 2, yPos, { align: 'right' });
  yPos += 7;

  // Balance Due with red background
  doc.setFillColor(245, 245, 245);
  doc.rect(totalsX, yPos - 3.5, totalsWidth, 7, 'F');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(225, 71, 50);
  doc.text('Balance Due', totalsX + 2, yPos);
  doc.text(`${invoiceData.balanceDue.toFixed(2)} INR`, totalsX + totalsWidth - 2, yPos, { align: 'right' });
  yPos += 10;

  // Total in words
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  const amountInWords = `Total In Words:  ${numberToWords(invoiceData.grandTotal)}`;
  const wordsLines = doc.splitTextToSize(amountInWords, pageWidth - 2 * margin);
  doc.text(wordsLines, margin, yPos);
  yPos += wordsLines.length * 4 + 5;

  // Notes section
  if (invoiceData.notes && invoiceData.notes.trim()) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes', margin, yPos);
    yPos += 5;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const notesLines = doc.splitTextToSize(invoiceData.notes, pageWidth - 2 * margin);
    doc.text(notesLines, margin, yPos);
    yPos += notesLines.length * 4 + 5;
  }

  // Bank details
  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = margin;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Account Name: ${invoiceData.billedFrom.name}`, margin, yPos);
  yPos += 5;
  doc.setFont('helvetica', 'normal');
  doc.text('Account No: 778305000125', margin, yPos);
  yPos += 4;
  doc.text('Bank Name: ICICI Bank', margin, yPos);
  yPos += 4;
  doc.text('IFSC/NEFT Code: ICIC0007783', margin, yPos);
  yPos += 8;

  // Terms & Conditions
  if (invoiceData.termsConditions && invoiceData.termsConditions.trim()) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', margin, yPos);
    yPos += 5;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const termsLines = doc.splitTextToSize(invoiceData.termsConditions, pageWidth - 2 * margin);
    doc.text(termsLines, margin, yPos);
    yPos += termsLines.length * 4 + 2;
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions', margin, yPos);
    yPos += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('All disputes are subject to Delhi Jurisdiction only.', margin, yPos);
    yPos += 8;
  }

  // Authorized Signature
  yPos = pageHeight - 25;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Authorized Signature ____________________', pageWidth - margin, yPos, { align: 'right' });

  // Footer with website
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('www.manakengineering.com', pageWidth / 2, pageHeight - 10, { align: 'center' });

  // Download or preview
  if (action === 'download') {
    doc.save(`Invoice-${invoiceData.invoiceNumber}.pdf`);
  } else {
    // Open in new window for preview
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  }

  return doc;
};
